import API from './api';

export const getUsers = async () => {
  try {
    const response = await API.get('/users');
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const createUser = async (userData) => {
  try {
    const response = await API.post('/users/register', userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (mobileNumber, userData) => {
  try {
    const response = await API.put(`/users/${mobileNumber}`, userData);
    return response.data;
  } catch (error) {
    console.error(`Error updating user ${mobileNumber}:`, error);
    throw error;
  }
};

export const deleteUser = async (mobileNumber) => {
  try {
    const response = await API.delete(`/users/${mobileNumber}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting user ${mobileNumber}:`, error);
    throw error;
  }
};

export const resetDeviceId = async (mobileNumber) => {
  try {
    const response = await API.post(`/users/${mobileNumber}/reset-device-id`);
    return response.data;
  } catch (error) {
    console.error('Error resetting device ID:', error);
    throw error;
  }
};

export const resetAllDeviceIds = async () => {
  try {
    const response = await API.post('/users/reset-all-device-ids');
    return response.data;
  } catch (error) {
    console.error('Error resetting all device IDs:', error);
    throw error;
  }
};

export const toggleSupervisorLeave = async (mobileNumber) => {
  try {
    const response = await API.post('/users/toggleleave', { mobileNumber });
    return response.data;
  } catch (error) {
    console.error('Error toggling supervisor leave status:', error);
    throw error;
  }
};

export const getUserProfile = async (mobileNumber) => {
  try {
    const response = await API.get(`/users/${mobileNumber}/profile`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user profile for ${mobileNumber}:`, error);
    throw error;
  }
};

export const loginWorkerSupervisor = async (credentials) => {
  try {
    const response = await API.post('/users/login', credentials);
    return response.data;
  } catch (error) {
    console.error('Error logging in worker/supervisor:', error);
    throw error;
  }
};

export const requestAccountDeletion = async (mobileNumber, message) => {
  try {
    const response = await API.post('/users/request-deletion', { mobileNumber, message });
    return response.data;
  } catch (error) {
    console.error('Error requesting account deletion:', error);
    throw error;
  }
};

export const listUsers = async () => {
  try {
    const response = await API.get('/users');
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const listDeletedUsers = async () => {
  try {
    const response = await API.get('/users/deleted');
    return response.data;
  } catch (error) {
    console.error('Error fetching deleted users:', error);
    throw error;
  }
};

export const restoreUser = async (mobileNumber) => {
  try {
    const response = await API.post(`/users/${mobileNumber}/restore`);
    return response.data;
  } catch (error) {
    console.error(`Error restoring user ${mobileNumber}:`, error);
    throw error;
  }
};

// Admin Management Functions
export const deleteAdmin = async (email) => {
  try {
    const response = await API.delete(`/admins/${email}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting admin ${email}:`, error);
    throw error;
  }
};

export const restoreAdmin = async (email) => {
  try {
    const response = await API.post(`/admins/${email}/restore`);
    return response.data;
  } catch (error) {
    console.error(`Error restoring admin ${email}:`, error);
    throw error;
  }
};

export const listDeletedAdmins = async () => {
  try {
    const response = await API.get('/admins/deleted');
    return response.data;
  } catch (error) {
    console.error('Error fetching deleted admins:', error);
    throw error;
  }
};

// Role-based access control functions
export const checkAdminAccess = async (requiredRole) => {
  try {
    const response = await API.get(`/admins/check-access?requiredRole=${requiredRole}`);
    return response.data;
  } catch (error) {
    console.error(`Error checking admin access for role ${requiredRole}:`, error);
    throw error;
  }
};

export const getUserPermissions = async () => {
  try {
    const response = await API.get('/admins/permissions');
    return response.data;
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    throw error;
  }
};

export const createAdmin = async (adminData) => {
  try {
    const response = await API.post('/admins/register', adminData);
    return response.data;
  } catch (error) {
    console.error('Error creating admin:', error);
    throw error;
  }
};

export const updateAdmin = async (email, adminData) => {
  try {
    const response = await API.put(`/admins/${email}`, adminData);
    return response.data;
  } catch (error) {
    console.error(`Error updating admin ${email}:`, error);
    throw error;
  }
};

export const listAdmins = async () => {
  try {
    const response = await API.get('/admins');
    return response.data;
  } catch (error) {
    console.error('Error fetching admins:', error);
    throw error;
  }
};

// Utility function to normalize admin roles - blank roles become ADMIN
// This ensures backward compatibility for existing users without explicit roles
export const normalizeAdminRole = (role) => {
  if (!role || role.trim() === '') {
    return 'ADMIN';
  }
  return role.trim();
};

// Function to get user permissions with role normalization
export const getUserPermissionsWithNormalization = async () => {
  try {
    const response = await API.get('/admins/permissions');
    const data = response.data;
    
    // Normalize the role if it's blank
    if (data.userRole) {
      data.userRole = normalizeAdminRole(data.userRole);
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    throw error;
  }
};

// Get unique payment accounts for autofill suggestions
export const getUniquePaymentAccounts = async () => {
  try {
    const response = await API.get('/users/payment-accounts');
    return response.data;
  } catch (error) {
    console.error('Error fetching unique payment accounts:', error);
    throw error;
  }
};

// Get unique jobs for autofill suggestions
export const getUniqueJobs = async () => {
  try {
    const response = await API.get('/users/jobs');
    return response.data;
  } catch (error) {
    console.error('Error fetching unique jobs:', error);
    throw error;
  }
};

// Temporary User Management Functions
export const createTemporaryUser = async (userData) => {
  try {
    const response = await API.post('/users/temporary', userData);
    return response.data;
  } catch (error) {
    console.error('Error creating temporary user:', error);
    throw error;
  }
};

export const listTemporaryUsers = async () => {
  try {
    const response = await API.get('/users/temporary');
    return response.data;
  } catch (error) {
    console.error('Error fetching temporary users:', error);
    throw error;
  }
};

export const convertTemporaryToPermanent = async (mobileNumber, userData) => {
  try {
    const response = await API.post(`/users/temporary/${mobileNumber}/convert`, userData);
    return response.data;
  } catch (error) {
    console.error(`Error converting temporary user ${mobileNumber} to permanent:`, error);
    throw error;
  }
}; 